// @greenlight-spec: 11-observability.md
// @greenlight-spec: 26-versioning-update-strategy.md
import { z } from 'zod';
import { INSTALL_CLOUD_PROVIDERS } from './providers.js';
/**
 * Which cloud this install runs on — the same discriminator
 * `GREENLIGHT_CLOUD_PROVIDER` names for the provider registry (docs/18). The
 * fleet reads it so installation and version views name a cloud without
 * inferring one from an Azure-shaped field.
 */
export const InstallCloudProviderSchema = z.enum(INSTALL_CLOUD_PROVIDERS);
/**
 * Identity of the infrastructure-as-code package the running release was
 * installed from: which language the packaged installer is written in, and the
 * digest the updater verified the archive against (docs/26 §Release Artifact
 * Set). Discriminated so `unknown` cannot carry a digest — an install that has
 * no digest to report says so rather than reporting an empty one.
 *
 * `unknown` covers a local or fake provider, an install whose chart predates
 * the injected digest, and a digest that does not parse — the collector reports
 * the sentinel rather than a value it cannot vouch for.
 */
export const PROVIDER_IAC_DIGEST_PATTERN = /^sha256:[a-f0-9]{64}$/;
const iacDigestSchema = z.string().regex(PROVIDER_IAC_DIGEST_PATTERN, 'must be sha256:<64 hex>');
/**
 * Canonical `sha256:<hex>` form of a release-manifest digest. The manifest
 * records provider archive digests as bare hex while every digest Greenlight
 * reports elsewhere is self-describing, so the prefix is added on the way in
 * rather than leaving each reader to guess which form it holds.
 */
function canonicalIacDigest(digest) {
    const trimmed = digest.trim().toLowerCase();
    const prefixed = trimmed.startsWith('sha256:') ? trimmed : `sha256:${trimmed}`;
    return PROVIDER_IAC_DIGEST_PATTERN.test(prefixed) ? prefixed : null;
}
export const ProviderIacPackageSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('bicep'), digest: iacDigestSchema }),
    z.object({ kind: z.literal('terraform'), digest: iacDigestSchema }),
    z.object({ kind: z.literal('unknown') }),
]);
/** Which packaged installer each cloud ships (docs/26 §Release Artifact Set). */
const IAC_KIND_BY_CLOUD = {
    azure: 'bicep',
    gcp: 'terraform',
    aws: 'terraform',
};
/**
 * The IaC package a cloud + injected digest describe. A cloud with no digest,
 * or one whose digest is malformed, reports `unknown`: the fleet must be able
 * to tell "this install did not report a digest" from "this install is on
 * package X", and a blank digest reads as the latter.
 */
export function providerIacPackageOf(cloud, digest) {
    if (cloud === null || digest === undefined)
        return { kind: 'unknown' };
    const canonical = canonicalIacDigest(digest);
    return canonical === null
        ? { kind: 'unknown' }
        : { kind: IAC_KIND_BY_CLOUD[cloud], digest: canonical };
}
//# sourceMappingURL=provider-iac-package.js.map